import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Cancel, ArrowBack } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import SettingsSidebar from '../../components/Common/SettingsSidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  color?: string;
  is_system: boolean;
  is_active: boolean;
}

const EditUser: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role_id: '',
    status: 'active',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const emailTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchRoles();
    if (id) {
      fetchUser();
    }
  }, [id]);

  // Trigger email validation when email field changes (including auto-fill)
  useEffect(() => {
    if (formData.email && /\S+@\S+\.\S+/.test(formData.email)) {
      // Clear previous timeout
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      
      // Set new timeout for validation
      emailTimeoutRef.current = setTimeout(() => {
        validateEmailRealTime(formData.email);
      }, 800);
    }
  }, [formData.email]);

  const fetchRoles = async () => {
    try {
      const response = await apiService.getRoles();
      setRoles(response.data.data || response.data);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      toast.error(error.message || 'Failed to fetch roles');
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUser(parseInt(id!));
      const user = response.data.data || response.data;
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        confirmPassword: '',
        role_id: user.roles?.[0]?.id?.toString() || '',
        status: user.status || 'active',
      });
    } catch (error: any) {
      console.error('Error fetching user:', error);
      toast.error(error.message || 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  };

  const validateEmailRealTime = async (email: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      return;
    }

    try {
      const response = await apiService.validateEmail(email, parseInt(id!));
      console.log('Email validation response:', response);
      
      if (response.data && !response.data.available) {
        setErrors(prev => ({ ...prev, email: response.data.message }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    } catch (error: any) {
      console.error('Email validation error:', error);
      // Handle validation errors from the API
      if (error.response && error.response.data && error.response.data.message) {
        setErrors(prev => ({ ...prev, email: error.response.data.message }));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Real-time email validation
    if (name === 'email' && value) {
      // Clear previous timeout
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      
      // Set new timeout
      emailTimeoutRef.current = setTimeout(() => {
        validateEmailRealTime(value);
      }, 800);
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user selects
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.role_id) {
      newErrors.role_id = 'Role is required';
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const userData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role_id: parseInt(formData.role_id),
        status: formData.status,
      };

      // Only include password if it's provided
      if (formData.password) {
        userData.password = formData.password;
        userData.password_confirmation = formData.confirmPassword;
      }

      await apiService.updateUser(parseInt(id!), userData);
      toast.success('User updated successfully');
      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Handle validation errors from the API
      if (error.response && error.response.data && error.response.data.errors) {
        const apiErrors = error.response.data.errors;
        const newErrors: Record<string, string> = {};
        
        // Map API errors to form errors
        if (apiErrors.email) {
          newErrors.email = apiErrors.email[0];
        }
        if (apiErrors.name) {
          newErrors.name = apiErrors.name[0];
        }
        if (apiErrors.phone) {
          newErrors.phone = apiErrors.phone[0];
        }
        if (apiErrors.password) {
          newErrors.password = apiErrors.password[0];
        }
        if (apiErrors.role_id) {
          newErrors.role_id = apiErrors.role_id[0];
        }
        
        setErrors(newErrors);
      } else {
        toast.error(error.message || 'Failed to update user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Edit User
        </Typography>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: '12px',
            borderColor: '#e5e7eb',
            backgroundColor: 'white',
            color: '#374151',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              borderColor: '#d1d5db',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Cancel
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Sidebar - Settings Menu */}
        <Grid size={{ xs: 12, md: 3 }}>
          <SettingsSidebar />
        </Grid>

        {/* Right Content Area */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: 'calc(100vh - 160px)' }}>
            <CardContent sx={{ p: 3, height: '100%', overflow: 'auto' }}>
              {/* Form Header */}
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
                Edit User
              </Typography>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Basic Information */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Basic Information
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth required>
                            <InputLabel>Role</InputLabel>
                            <Select
                              name="role_id"
                              value={formData.role_id}
                              onChange={handleSelectChange}
                              label="Role"
                              error={!!errors.role_id}
                              sx={{
                                borderRadius: '8px',
                              }}
                            >
                              {roles.map((role) => (
                                <MenuItem key={role.id} value={role.id}>
                                  {role.display_name || role.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {errors.role_id && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                              {errors.role_id}
                            </Typography>
                          )}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                              name="status"
                              value={formData.status}
                              onChange={handleSelectChange}
                              label="Status"
                              sx={{
                                borderRadius: '8px',
                              }}
                            >
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="inactive">Inactive</MenuItem>
                              <MenuItem value="suspended">Suspended</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Password Section */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3, backgroundColor: '#f9fafb' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                        Password (Optional - Leave blank to keep current password)
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="New Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                </Grid>

                {/* Form Footer */}
                <Box sx={{ 
                  mt: 4, 
                  pt: 3, 
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f8fafc',
                  borderRadius: '0 0 12px 12px',
                  margin: '0 -24px -24px -24px',
                  padding: '24px'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      onClick={handleSubmit}
                      sx={{
                        px: 4,
                        py: 2,
                        borderRadius: '8px',
                        backgroundColor: '#4A90E2',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1rem',
                        textTransform: 'none',
                        minWidth: '140px',
                        '&:hover': {
                          backgroundColor: '#3b82f6',
                          boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
                        },
                        '&:disabled': {
                          backgroundColor: '#9ca3af',
                          color: 'white',
                        },
                      }}
                    >
                      {loading ? 'Updating...' : 'Update User'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default EditUser;
